import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ScrollText, Eye, Pencil, Check, Loader2, AlertCircle, GripVertical } from 'lucide-react';
import PanelShell from './PanelShell';
import ChatMessages from '../ChatMessages';
import ChatInput from '../ChatInput';
import AttachPickerModal from '../AttachPickerModal';
import apiService from '../../services/apiService';
import { documentService } from '../../services/documentService';
import { workspaceService } from '../../services/workspaceService';
import { mapDbMessage } from '../../utils/messageMapper';
import { composeSnapshot } from '../../utils/composeSnapshot';
import { isDocumentFile } from '../../utils/constants';
import { avatarInitials } from '../../utils/avatar';
import { debounce } from '../../utils/debounce';
import { useLanguage } from '../../i18n';
import useAuth from '../../hooks/useAuth';
import { useApiStatus } from '../../hooks/useApiStatus';
import { useComposerVoiceSegments } from '../../hooks/useComposerVoiceSegments';
import { makeAttachmentDescriptor } from '../../hooks/useFileAttachment';
import { logger } from '../../utils/logger';
import { userErrorMessage } from '../../utils/userError';

// ─────────────────────────────────────────────────────────────────────────
// RulebookPanel — PAI Rulebook (#146), Part A.
//
// A horizontal split view:
//
//   LEFT  "The Folio"          — the user's standing rules as markdown. Stored
//                                as the RULEBOOK workspace file and injected
//                                into PAI's system instruction on EVERY channel
//                                (see BE rulebook_service.get_rulebook_context),
//                                so a rule written here is obeyed in the Main
//                                Chat and Telegram alike.
//   RIGHT "The Assistant Desk" — a persistent conversation (channel='rulebook')
//                                for drafting rules with PAI. It keeps its
//                                history between visits.
//
// Responsive is CONTAINER-DRIVEN (a ResizeObserver on the body, not a viewport
// breakpoint — the #142 pattern): side-by-side when the panel is wide, stacked
// when it is a narrow split column or on mobile. Each half scrolls internally
// and every box is min-w-0 so nothing escapes the panel (#243).
//
// The Folio autosaves: a debounced PUT ~800ms after typing stops, plus an
// immediate flush on blur and on unmount, so closing the panel never loses a
// keystroke.
// ─────────────────────────────────────────────────────────────────────────

const AUTOSAVE_MS = 800;
// Deliberately BELOW the width of a default docked split column (~590px on a
// 1280px viewport): the ticket specifies a HORIZONTAL split view, so the two
// halves must sit side by side there. Stacking is the narrow-column / mobile
// containment fallback (#243), not the normal case.
const STACK_BREAKPOINT = 520;

// ── Resizable divider (#383) ─────────────────────────────────────────────
// Mirrors MainOfficeView's SplitDivider idiom (named floors on both sides,
// `cursor-col-resize`, persisted state) but drives an absolute pixel WIDTH
// for the Desk rather than a fraction of the panel — the ticket's own floor
// is stated in pixels ("matching its current default size"), and a fixed
// width is what the panel already used before this ticket (330px).
const DESK_WIDTH_KEY = 'omnis:rulebook:desk-width';
const DESK_DEFAULT_WIDTH = 330; // the panel's pre-#383 fixed Desk width
// The narrowest the Folio has EVER rendered by default, pre-#383: at
// STACK_BREAKPOINT — the narrowest width this horizontal split ever renders
// at — the old `max-w-[45%]` cap on the Desk dominated its fixed 330px
// (45% of 520 = 234px), leaving the Folio 520 - 234 = 286px. 300px keeps the
// ticket's "never narrower than its current default size" promise for EVERY
// panel width this split can render at, with a clean round number.
const FOLIO_MIN_WIDTH = 300;
// The Desk hosts the real Main-Chat composer (#173) — reuse ITS floor
// (`CHAT_MIN_WIDTH` in MainOfficeView.jsx) rather than inventing a second
// number for the same `<ChatInput>` chrome.
const DESK_MIN_WIDTH = 240;
const DIVIDER_WIDTH = 14; // matches MainOfficeView's SplitDivider (`w-[14px]`)
const KEYBOARD_STEP = 24;

const loadDeskWidth = () => {
    try {
        const raw = localStorage.getItem(DESK_WIDTH_KEY);
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : DESK_DEFAULT_WIDTH;
    } catch (_) {
        return DESK_DEFAULT_WIDTH;
    }
};

const persistDeskWidth = (width) => {
    try {
        localStorage.setItem(DESK_WIDTH_KEY, String(Math.round(width)));
    } catch (_) { /* ignore */ }
};

// The one place both floors are enforced, so a mouse-drag, a keyboard nudge,
// and a container resize (window resize, the divider dragged in
// MainOfficeView, a panel expand/collapse) can never disagree about what a
// legal Desk width is. `containerWidth` may be unknown (e.g. before the
// panel has laid out) — in that case only the Desk's own floor applies.
const clampDeskWidth = (desired, containerWidth) => {
    const min = DESK_MIN_WIDTH;
    const max = Number.isFinite(containerWidth)
        ? Math.max(min, containerWidth - DIVIDER_WIDTH - FOLIO_MIN_WIDTH)
        : Infinity;
    return Math.min(max, Math.max(min, desired));
};

// Vertical drag handle between the Folio and the Desk. Unlike
// MainOfficeView's near-invisible hover-only rail, the ticket asks for a
// handle that is CLEARLY VISIBLE at rest — so it renders a permanent grip
// pill rather than only brightening on hover.
const RulebookDivider = ({ containerRef, deskWidth, onWidthChange, t }) => {
    const draggingRef = useRef(false);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        draggingRef.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        const handleMove = (ev) => {
            if (!draggingRef.current || !containerRef?.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            // The Desk sits to the RIGHT of the divider, so its width is the
            // distance from the pointer to the container's right edge.
            onWidthChange(rect.right - ev.clientX, rect.width);
        };
        const handleUp = () => {
            draggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        };
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
    }, [containerRef, onWidthChange]);

    // Keyboard resize, so the handle is not a focus trap: ArrowLeft moves the
    // handle left (the Desk gets wider, mirroring a leftward drag), ArrowRight
    // moves it right (the Desk gets narrower).
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            onWidthChange(deskWidth + KEYBOARD_STEP, containerRef?.current?.getBoundingClientRect().width);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            onWidthChange(deskWidth - KEYBOARD_STEP, containerRef?.current?.getBoundingClientRect().width);
        }
    }, [deskWidth, containerRef, onWidthChange]);

    const label = t('msgDragToResize', 'Drag to resize');

    return (
        <div
            data-testid="rulebook-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label={label}
            title={label}
            tabIndex={0}
            onMouseDown={handleMouseDown}
            onKeyDown={handleKeyDown}
            className="group relative flex-shrink-0 flex items-center justify-center cursor-col-resize focus:outline-none"
            style={{ width: `${DIVIDER_WIDTH}px` }}
        >
            <span className="w-px h-full bg-pai-hairline group-hover:bg-white/30 group-focus:bg-white/40 transition-colors" />
            <span className="absolute w-[18px] h-10 rounded-full bg-white/10 border border-pai-subtle flex items-center justify-center transition-colors group-hover:bg-white/20 group-hover:border-pai-strong group-focus:bg-white/20 group-focus:border-pai-strong">
                <GripVertical size={12} className="text-white/55 group-hover:text-white/85 group-focus:text-white/85" />
            </span>
        </div>
    );
};

// The Folio surface. Per the ticket it is theme-aware: WHITE in light mode,
// SYSTEM BLUE in dark mode (not the usual gray chrome). Applied to the editor
// and the preview identically so toggling never changes the paper.
//
// ⚠️ This is the ONE place in this panel that keeps a `dark:` variant. The paper
// is the Rulebook's identity — a deliberately opaque sheet that must stay
// distinguishable from the ordinary translucent panes — and `rulebook.spec.js`
// asserts its computed background in BOTH themes (#ffffff / blue-950 #172554).
const FOLIO_SURFACE = 'bg-white dark:bg-blue-950 text-gray-900 dark:text-blue-50';

// The design's long-form reading measure, shared by the paper's editor and its
// rendered preview so toggling never re-flows the text.
const FOLIO_TYPE = 'text-[15px] leading-[1.65] [text-wrap:pretty]';

const SaveState = ({ state, t }) => {
    const map = {
        saving: { Icon: Loader2, cls: 'text-pai-meta animate-spin', label: t('rulebookSaving', 'Saving…') },
        saved: { Icon: Check, cls: 'text-pai-green', label: t('rulebookSaved', 'Saved') },
        error: { Icon: AlertCircle, cls: 'text-pai-red', label: t('rulebookSaveFailed', "Couldn't save") },
    };
    const entry = map[state];
    return (
        <span
            data-testid="rulebook-save-state"
            data-state={state}
            className="flex items-center gap-1.5 text-[12.5px] text-pai-meta min-w-0"
        >
            {entry && (
                <>
                    <entry.Icon size={12} className={`${entry.cls} flex-shrink-0`} />
                    <span className="truncate">{entry.label}</span>
                </>
            )}
        </span>
    );
};

// ── LEFT: The Folio ──────────────────────────────────────────────────────
const Folio = ({ t, stacked, minWidthPx }) => {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [saveState, setSaveState] = useState('idle'); // idle|saving|saved|error
    const [preview, setPreview] = useState(false);

    // Live mirror of the text so the blur/unmount flush always writes the
    // LATEST keystroke rather than whatever the closure captured.
    const contentRef = useRef('');
    // What the server is known to hold — lets us skip no-op writes.
    const savedRef = useRef(null);

    // The server RE-NUMBERS *and* REFLOWS the Folio on every save — gapless
    // 1..N, plus exactly one blank line between rule blocks (#344 addendum:
    // deleting a rule used to leave its blank lines behind, so a deleted
    // 20-line rule left a 20-line hole and the rule below never snapped up).
    // So what it stored can differ from what we sent. Adopt its copy and both
    // the numbers and the vertical rhythm settle the moment the save lands —
    // that is the whole of "Manual Entry Synchronization": no refetch, and no
    // client-side numbering or spacing logic here.
    //
    // ⚠️ Only when the buffer is still EXACTLY what we sent. A keystroke made
    // while the save was in flight must never be clobbered mid-typing — the
    // next save renumbers again, so nothing is lost by skipping this one.
    const save = useCallback(async (text) => {
        if (savedRef.current === text) return;
        setSaveState('saving');
        try {
            const updated = await workspaceService.updateFile('RULEBOOK', text);
            const stored = typeof updated?.content === 'string' ? updated.content : text;
            savedRef.current = stored;
            if (contentRef.current === text && stored !== text) {
                setContent(stored);
                contentRef.current = stored;
            }
            setSaveState('saved');
        } catch (err) {
            logger.error('Failed to save the Rulebook folio', err);
            setSaveState('error');
        }
    }, []);

    const debouncedSave = useMemo(() => debounce(save, AUTOSAVE_MS), [save]);

    const load = useCallback(async () => {
        try {
            // GET creates the file seeded with the ticket's header on first open.
            const file = await workspaceService.getFile('RULEBOOK');
            const text = file?.content ?? '';
            setContent(text);
            contentRef.current = text;
            savedRef.current = text;
        } catch (err) {
            logger.error('Failed to load the Rulebook folio', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // The Add-Rule card writes the Folio server-side and then dispatches this
    // event so the paper shows the new rule live.
    //
    // DIRTY-STATE GUARD: a blind refetch would overwrite whatever the user is
    // currently typing (their keystrokes are only ~800ms from being saved, and
    // the server's copy does not have them yet). So when there ARE unsaved local
    // edits, merge the appended rule into the live text instead and let the
    // ordinary autosave persist the merge. When the buffer is clean, refetch —
    // the server is authoritative and its copy already carries the new rule.
    useEffect(() => {
        const onUpdated = (event) => {
            const dirty = savedRef.current !== null && savedRef.current !== contentRef.current;
            const ruleText = event?.detail?.rule_text;
            if (!dirty || !ruleText) {
                load();
                return;
            }
            const merged = `${contentRef.current.replace(/\s+$/, '')}\n\n${ruleText}\n`;
            setContent(merged);
            contentRef.current = merged;
            setSaveState('idle');
            debouncedSave(merged);
        };
        window.addEventListener('omnis:rulebook-updated', onUpdated);
        return () => window.removeEventListener('omnis:rulebook-updated', onUpdated);
    }, [load, debouncedSave]);

    // Flush on unmount (panel close / navigation) — cancel the pending timer
    // and write the latest text immediately.
    useEffect(() => () => {
        debouncedSave.cancel();
        if (savedRef.current !== null && savedRef.current !== contentRef.current) {
            workspaceService.updateFile('RULEBOOK', contentRef.current)
                .catch((err) => logger.error('Failed to flush the Rulebook folio', err));
        }
    }, [debouncedSave]);

    const handleChange = (e) => {
        const text = e.target.value;
        setContent(text);
        contentRef.current = text;
        setSaveState('idle');
        debouncedSave(text);
    };

    const handleBlur = () => {
        debouncedSave.cancel();
        save(contentRef.current);
    };

    return (
        <section
            data-testid="rulebook-folio-column"
            style={minWidthPx ? { minWidth: `${minWidthPx}px` } : undefined}
            className={`flex flex-col min-w-0 min-h-0 flex-1 border-pai-hairline ${stacked ? 'border-b' : ''}`}
        >
            <header className="flex items-center gap-2.5 px-5 py-4 border-b border-pai-hairline flex-shrink-0 min-w-0">
                <ScrollText size={18} className="text-white flex-shrink-0" />
                <h3 className="text-[22px] font-bold tracking-[-0.02em] text-white truncate min-w-0">
                    {t('rulebookFolio', 'The Folio')}
                </h3>
                <div className="ml-auto flex items-center gap-2 min-w-0">
                    <SaveState state={saveState} t={t} />
                    <button
                        type="button"
                        data-testid="rulebook-preview-toggle"
                        onClick={() => setPreview((v) => !v)}
                        title={preview ? t('rulebookEdit', 'Edit') : t('rulebookPreview', 'Preview')}
                        aria-label={preview ? t('rulebookEdit', 'Edit') : t('rulebookPreview', 'Preview')}
                        className="pai-icon-btn w-9 h-9 flex-shrink-0"
                    >
                        {preview ? <Pencil size={16} /> : <Eye size={16} />}
                    </button>
                </div>
            </header>

            {/* The paper. A rule card in the handoff is a bordered block holding a
                title and a 70ch body; here the Folio is ONE free-markdown sheet
                (the ticket's design — the server owns the numbering), so the card
                treatment lands on the sheet itself and the reading measure on its
                rendered rules. */}
            <div className="flex-1 min-h-0 min-w-0 p-5">
                {preview ? (
                    <div
                        data-testid="rulebook-folio-preview"
                        className={`h-full w-full min-w-0 overflow-auto rounded-card border border-pai px-[26px] py-6 prose prose-sm dark:prose-invert max-w-none break-words [overflow-wrap:anywhere] prose-headings:text-[19px] prose-headings:font-bold prose-headings:tracking-[-0.015em] prose-p:max-w-[70ch] prose-li:max-w-[70ch] ${FOLIO_TYPE} ${FOLIO_SURFACE}`}
                    >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                ) : (
                    <textarea
                        data-testid="rulebook-folio"
                        value={content}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={loading}
                        spellCheck={false}
                        placeholder={t('rulebookFolioPlaceholder', 'Write the rules PAI must follow…')}
                        aria-label={t('rulebookFolio', 'The Folio')}
                        className={`h-full w-full min-w-0 resize-none rounded-card border border-pai px-[26px] py-6 focus:outline-none focus:border-white/80 focus:shadow-glow-sm placeholder-gray-400 dark:placeholder-blue-300/50 transition-[border-color,box-shadow] duration-150 ${FOLIO_TYPE} ${FOLIO_SURFACE}`}
                    />
                )}
            </div>
        </section>
    );
};

// ── RIGHT: The Assistant Desk ────────────────────────────────────────────
/**
 * The Desk — a drafting table with the SAME input stack as the Main Chat
 * ("Rulebook messages").
 *
 * GROUND TRUTH = the Main Chat, and parity is achieved by REUSE rather than
 * re-implementation — the DayChannelPanel Focus workspace (#173) is the
 * precedent this follows line for line. The thread is the real
 * <ChatMessages>/<Message> stack and the composer is the real <ChatInput>, so
 * the "+" attachment menu, the multi-clip voice tray, the recording bar, the
 * pre-send transcription editor and the Stop control are byte-identical to the
 * Main Chat by construction, not by imitation. Only the lean per-turn STATE
 * lives here: the Desk is bound to exactly ONE conversation (the user has
 * exactly one), so there is no conversation switching and therefore no drafts,
 * no LRU message buffers and no origin-bound upload binding.
 *
 * The turn itself still runs on the SCRIBE (`/rulebook/desk-stream`), never the
 * agentic loop — the Desk's whole point is that it drafts rules and touches
 * nothing else. What the composer swap adds is the SOURCES a turn can carry:
 * typed text, N voice clips, and attached documents whose text the scribe reads
 * server-side.
 *
 * Deliberately NOT adopted from the Main Chat: reply threading and message
 * editing (the thread stays `isReadOnly`). Both would need a truncate-and-re-run
 * path that the scribe stream does not have, and the card is scoped to the
 * INPUT stack.
 */
const AssistantDesk = ({ t, userInitials, stacked, widthPx }) => {
    const [messages, setMessages] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [input, setInput] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [sending, setSending] = useState(false);
    // Send pressed while a clip is still transcribing → flush automatically once
    // every transcription settles (the Main Office "wait-and-send" contract).
    const [pendingSend, setPendingSend] = useState(false);
    const [docPickerOpen, setDocPickerOpen] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const uploadAbortersRef = useRef({});
    const streamAbortRef = useRef(null);
    // The Desk is a persistent conversation of its own (#377 attribution).
    const voice = useComposerVoiceSegments(conversationId);
    // The reused <ChatInput> gates Send/Mic/"+" on a live backend exactly as it
    // does in the Main Office, so the Desk composer obeys the same contract.
    const { apiStatus } = useApiStatus(apiService);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const desk = await workspaceService.getRulebookDesk();
                if (cancelled) return;
                setConversationId(desk?.conversation_id || null);
                setMessages((desk?.messages || []).map(mapDbMessage));
            } catch (err) {
                logger.error('Failed to load the Assistant Desk', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // ── Attachments ("+" → Upload from Device / Select from File Manager) ──
    // The same two-source pipeline as the Main Chat and the bucket composer:
    // a device file is uploaded first and then referenced by id, an existing
    // document is referenced without re-uploading. Files land in the shared
    // "Conversation Uploads" system folder — the Desk IS a conversation, so its
    // uploads belong exactly where every other chat upload goes; a dedicated
    // "Rulebook Uploads" folder would fragment the File Manager for no gain.
    const uploadOne = useCallback(async (file, folderId, tempId) => {
        const aborter = new AbortController();
        uploadAbortersRef.current[tempId] = aborter;
        try {
            // #404 — the Desk is a conversation, so attached-image OCR is billed
            // to it rather than landing unattributed.
            const result = await documentService.uploadDocumentToFolder(
                file, folderId, null, null, aborter.signal, conversationId,
            );
            const doc = result?.document || result?.documents?.[0] || result;
            if (!doc?.id) throw new Error('Upload did not return a document id');
            setAttachedFiles((prev) => prev.map((f) => (f.tempId === tempId ? {
                ...f,
                id: doc.id,
                filename: doc.original_filename || doc.filename || file.name,
                mime_type: doc.mime_type || file.type,
                file_size: doc.file_size ?? file.size,
                is_image: (doc.mime_type || file.type || '').startsWith('image/'),
                uploading: false,
                upload_error: null,
            } : f)));
        } catch (err) {
            if (err?.aborted || aborter.signal.aborted) return; // pill already removed
            logger.error('Desk upload failed', err);
            setAttachedFiles((prev) => prev.map((f) => (f.tempId === tempId
                ? { ...f, uploading: false, upload_error: userErrorMessage(err) }
                : f)));
        } finally {
            delete uploadAbortersRef.current[tempId];
        }
    // conversationId: #404 threads it into the upload. It is async state
    // (null until the Desk loads), so [] would capture null forever.
    }, [conversationId]);

    const handleAddFiles = useCallback(async (files) => {
        const supported = [...(files || [])].filter((f) => isDocumentFile(f.name));
        if (supported.length === 0) return;
        let folderId = null;
        try {
            folderId = await documentService.ensureSystemFolder('conversation-uploads');
        } catch (err) {
            logger.error('Failed to ensure Conversation Uploads folder', err);
        }
        for (const file of supported) {
            const pending = makeAttachmentDescriptor(file);
            setAttachedFiles((prev) => [...prev, pending]);
            await uploadOne(file, folderId, pending.tempId);
        }
    }, [uploadOne]);

    const handleSelectExistingDocument = useCallback((docId, doc) => {
        const row = doc || {};
        const id = docId || row.id;
        if (!id) return;
        setAttachedFiles((prev) => {
            if (prev.some((f) => f.id === id)) return prev;
            const mime = row.mime_type || 'application/octet-stream';
            return [...prev, {
                tempId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                id,
                filename: row.original_filename || row.filename || 'Document',
                mime_type: mime,
                file_size: row.file_size ?? 0,
                is_image: mime.startsWith('image/') || !!row.is_image,
                uploading: false,
                upload_error: null,
            }];
        });
    }, []);

    const handleRemoveAttachment = useCallback((descriptor) => {
        const tempId = descriptor?.tempId;
        if (tempId && uploadAbortersRef.current[tempId]) {
            try { uploadAbortersRef.current[tempId].abort(); } catch (_) { /* noop */ }
            delete uploadAbortersRef.current[tempId];
        }
        setAttachedFiles((prev) => prev.filter((f) => (
            tempId ? f.tempId !== tempId : f.id !== descriptor?.id
        )));
    }, []);

    const handleClearAttachments = useCallback(() => {
        Object.values(uploadAbortersRef.current).forEach((a) => {
            try { a.abort(); } catch (_) { /* noop */ }
        });
        uploadAbortersRef.current = {};
        setAttachedFiles([]);
    }, []);

    // ── One Desk turn ────────────────────────────────────────────────────
    // The snapshot comes from the SHARED composeSnapshot(), so the Desk sends
    // byte-identical `[Typed]` / `[Voice N]` content, the same
    // `attached_documents` shape and the same corrected-transcription contract
    // as every other composer in the app.
    const dispatchSnapshot = useCallback(async (snap) => {
        if (!snap || !snap.backendContent) return;

        setInput('');
        setAttachedFiles([]);
        voice.clear();
        setError(null);
        setSending(true);

        const stamp = Date.now();
        const draftId = `desk-pending-${stamp}`;
        // The metadata the BE persists on the user's message. Exactly the keys
        // the shared mapDbMessage consumes, so a reloaded Desk rehydrates the
        // voice clips and attachment chips through the ordinary path.
        const messageMetadata = {};
        if (snap.attachedDocuments?.length) messageMetadata.attached_documents = snap.attachedDocuments;
        if (snap.dispatchSegments?.length) messageMetadata.voice_segments = snap.dispatchSegments;
        if (snap.typed) messageMetadata.typed_text = snap.typed;
        const hasMetadata = Object.keys(messageMetadata).length > 0;

        setMessages((prev) => [
            ...prev,
            {
                id: `desk-user-${stamp}`,
                content: snap.displayContent,
                isUser: true,
                timestamp: new Date(),
                metadata: hasMetadata ? messageMetadata : null,
                attachedDocuments: snap.attachedDocuments?.length ? snap.attachedDocuments : null,
                voiceSegments: snap.dispatchSegments?.length ? snap.dispatchSegments : null,
                typedText: snap.typed || null,
            },
            { id: draftId, content: '', isUser: false, timestamp: new Date(), metadata: null },
        ]);

        const patchDraft = (patch) => setMessages((prev) => prev.map(
            (m) => (m.id === draftId ? { ...m, ...patch } : m),
        ));

        const abortController = new AbortController();
        streamAbortRef.current = abortController;

        try {
            await workspaceService.streamRulebookDesk(snap.backendContent, {
                metadata: hasMetadata ? messageMetadata : undefined,
                signal: abortController.signal,
                onToken: (_piece, full) => patchDraft({ content: full }),
                // The card is attached only once the turn COMPLETES: the
                // `message_id` it must be resolved against arrives in the final
                // chunk, and the ticket puts the box at the end of the message
                // anyway.
                onDone: ({ content, card, messageId }) => patchDraft({
                    ...(messageId ? { id: messageId, dbId: messageId } : {}),
                    content,
                    metadata: card ? { rule_add_request: card } : null,
                }),
            });
        } catch (err) {
            if (abortController.signal.aborted) {
                // Stopped by the user — keep whatever streamed rather than
                // discarding a half-drafted rule.
                setMessages((prev) => prev.filter((m) => !(m.id === draftId && !m.content)));
            } else {
                logger.error('The Assistant Desk turn failed', err);
                // #439: a refusal keeps its sentence; anything else is the standard one.
                setError(userErrorMessage(err, t));
                // Drop the empty placeholder rather than leaving a blank bubble.
                setMessages((prev) => prev.filter((m) => !(m.id === draftId && !m.content)));
            }
        } finally {
            streamAbortRef.current = null;
            setSending(false);
        }
    }, [t, voice]);

    const buildSnapshot = useCallback(() => composeSnapshot({
        input, attachedFiles, voiceSegments: voice.segments,
    }), [input, attachedFiles, voice.segments]);

    const handleSend = useCallback(() => {
        if (sending || pendingSend || loading) return;
        if (attachedFiles.some((f) => f.uploading)) return;
        const snap = buildSnapshot();
        if (!snap) return;
        // Wait-and-send: queue until every clip's transcription has settled.
        if (voice.hasTranscribing) { setPendingSend(true); return; }
        dispatchSnapshot(snap);
    }, [sending, pendingSend, loading, attachedFiles, buildSnapshot, voice.hasTranscribing, dispatchSnapshot]);

    // Flush a queued send the moment the last transcription settles. Kept behind
    // a ref so the effect depends only on the two primitives that gate it.
    const flushRef = useRef(() => {});
    flushRef.current = () => dispatchSnapshot(buildSnapshot());
    useEffect(() => {
        if (!pendingSend || voice.hasTranscribing) return;
        setPendingSend(false);
        flushRef.current();
    }, [pendingSend, voice.hasTranscribing]);

    const handleStopStreaming = useCallback(() => {
        try { streamAbortRef.current?.abort(); } catch (_) { /* noop */ }
    }, []);

    return (
        // The handoff's assistant pane defaults to 330px, but is now a
        // RESIZABLE column (#383) — `widthPx` is driven by `RulebookPanel`'s
        // divider, clamped so the Folio can never be squeezed below its own
        // floor (see `clampDeskWidth`). Stacked mode drops the fixed width
        // entirely (there is no horizontal split to size there).
        <section
            data-testid="rulebook-desk"
            style={!stacked && widthPx ? { width: `${widthPx}px` } : undefined}
            className={`flex flex-col min-w-0 min-h-0 border-pai-hairline ${
                stacked ? 'flex-1' : 'flex-none border-l'
            }`}
        >
            <header className="flex items-center gap-3 px-5 py-4 border-b border-pai-hairline flex-shrink-0 min-w-0">
                <span
                    aria-hidden="true"
                    className="w-[38px] h-[38px] flex-none rounded-full bg-white text-pai-void flex items-center justify-center text-[12px] font-extrabold select-none"
                >
                    PAI
                </span>
                <h3 className="text-[15.5px] font-bold text-white truncate min-w-0">
                    {t('rulebookDesk', 'The Assistant Desk')}
                </h3>
            </header>

            {/* The thread is the REAL chat stack, so PAI's welcome, voice
                bubbles and attachment thumbnails all render exactly as they do
                in the Main Chat. Read-only: see the component docstring on why
                reply/edit are deliberately out of scope here. */}
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto px-2" data-testid="rulebook-desk-thread">
                <ChatMessages
                    messages={messages}
                    attachedFiles={attachedFiles}
                    messagesEndRef={messagesEndRef}
                    highlightedMessageId={null}
                    conversationId={conversationId}
                    replyingToMessages={[]}
                    isReadOnly
                    isLoadingConversation={loading}
                    userInitials={userInitials}
                />
            </div>

            {/* The scribe composer — the REAL <ChatInput>. Drafting stays
                strictly HERE: nothing composed on the Desk touches the Folio
                until the user presses Add Rule on the confirmation card.

                The wrapper carries `data-sending` because <ChatInput>'s Send
                button re-disables the instant the composer clears, so its
                enabled-ness cannot signal "the turn finished" — this attribute
                is the unambiguous streaming state (it replaces the one that
                used to sit on the bespoke `rulebook-desk-send` button). */}
            <div
                data-testid="rulebook-desk-composer"
                data-sending={sending ? 'true' : 'false'}
                className="flex-shrink-0 min-w-0"
            >
                {error && (
                    <div
                        data-testid="rulebook-desk-error"
                        className="px-4 pt-2 text-[13px] text-pai-red break-words"
                    >
                        {error}
                    </div>
                )}
                <ChatInput
                    input={input}
                    setInput={setInput}
                    attachedFiles={attachedFiles}
                    onRemoveAttachment={handleRemoveAttachment}
                    onClearAttachments={handleClearAttachments}
                    onSendMessage={handleSend}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                    }}
                    isLoading={false}
                    apiStatus={apiStatus}
                    canStop={sending}
                    onStopStreaming={handleStopStreaming}
                    onPasteFiles={handleAddFiles}
                    isRecording={voice.isRecording}
                    recordingDuration={voice.recordingDuration}
                    onStartRecording={voice.start}
                    onAcceptRecording={voice.accept}
                    onDiscardRecording={voice.discard}
                    voiceSegments={voice.segments}
                    onRemoveVoiceSegment={voice.remove}
                    onToggleSegmentTranscription={voice.toggleTranscription}
                    onEditSegmentTranscription={voice.editTranscription}
                    pendingSend={pendingSend}
                    onAddFiles={handleAddFiles}
                    onOpenFilePicker={() => setDocPickerOpen(true)}
                />
            </div>

            {docPickerOpen && (
                <AttachPickerModal
                    onClose={() => setDocPickerOpen(false)}
                    onSelect={(docId, doc) => { handleSelectExistingDocument(docId, doc); setDocPickerOpen(false); }}
                    testIds={{
                        modal: 'rulebook-attach-picker',
                        search: 'rulebook-attach-picker-search',
                        item: 'rulebook-attach-picker-item',
                    }}
                />
            )}
        </section>
    );
};

const RulebookPanel = ({ mode = 'split', onClose, onToggleExpand }) => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const userInitials = avatarInitials(user);

    const bodyRef = useRef(null);
    const [stacked, setStacked] = useState(false);
    // The Desk's resizable width (#383). Loaded once from localStorage; the
    // ResizeObserver below re-clamps it the instant the real container size
    // is known (its callback fires on the initial layout too), so a width
    // saved on a wider screen never leaves the Folio squeezed on return.
    const [deskWidth, setDeskWidth] = useState(loadDeskWidth);

    useEffect(() => {
        const el = bodyRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect?.width ?? el.clientWidth;
                setStacked(w < STACK_BREAKPOINT);
                setDeskWidth((prev) => clampDeskWidth(prev, w));
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // The ONE place a new Desk width is accepted — a mouse drag, a keyboard
    // nudge, and the ResizeObserver above all funnel through `clampDeskWidth`,
    // so none of them can disagree about what width is legal. Persisted on
    // every change (mirrors MainOfficeView's SplitDivider → usePanelSize).
    const handleDeskWidthChange = useCallback((desired, containerWidth) => {
        const width = containerWidth != null && Number.isFinite(containerWidth)
            ? containerWidth
            : bodyRef.current?.getBoundingClientRect().width;
        const clamped = clampDeskWidth(desired, width);
        setDeskWidth(clamped);
        persistDeskWidth(clamped);
    }, []);

    return (
        <PanelShell
            title={t('rulebook', 'Rulebook')}
            icon={<ScrollText size={18} />}
            mode={mode}
            onClose={onClose}
            onToggleExpand={onToggleExpand}
        >
            <div
                ref={bodyRef}
                data-testid="rulebook-panel"
                data-stacked={stacked ? 'true' : 'false'}
                className={`h-full min-h-0 min-w-0 flex ${stacked ? 'flex-col' : 'flex-row'}`}
            >
                <Folio t={t} stacked={stacked} minWidthPx={stacked ? undefined : FOLIO_MIN_WIDTH} />
                {!stacked && (
                    <RulebookDivider
                        containerRef={bodyRef}
                        deskWidth={deskWidth}
                        onWidthChange={handleDeskWidthChange}
                        t={t}
                    />
                )}
                <AssistantDesk
                    t={t}
                    userInitials={userInitials}
                    stacked={stacked}
                    widthPx={deskWidth}
                />
            </div>
        </PanelShell>
    );
};

export default RulebookPanel;
